class CurrencyLocalizationForm extends HTMLElement {
  static PANEL_TRANSITION_MS = 300;

  constructor() {
    super();
    this._initialized = false;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeyUp = this.onDocumentKeyUp.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onTriggerKeyDown = this.onTriggerKeyDown.bind(this);
    this.onOptionClick = this.onOptionClick.bind(this);
  }

  connectedCallback() {
    if (this._initialized) {
      return;
    }

    this.picker = this.querySelector('.currency-picker');
    this.form = this.querySelector('form');
    this.countryInput = this.querySelector('.currency-picker__country-input');
    this.trigger = this.querySelector('.currency-picker__trigger');
    this.panel = this.querySelector('.currency-picker__panel');
    this.options = this.querySelectorAll('.currency-picker__option');
    this.triggerLabel = this.querySelector('.currency-picker__trigger-label');

    if (!this.picker || !this.form || !this.countryInput || !this.trigger || !this.panel) {
      return;
    }

    this.countryInput.disabled = false;

    this.trigger.addEventListener('click', this.onTriggerClick);
    this.trigger.addEventListener('keydown', this.onTriggerKeyDown);

    this.options.forEach((option) => {
      option.addEventListener('click', this.onOptionClick);
    });

    this._initialized = true;
  }

  disconnectedCallback() {
    if (!this._initialized) {
      return;
    }

    this.trigger.removeEventListener('click', this.onTriggerClick);
    this.trigger.removeEventListener('keydown', this.onTriggerKeyDown);

    this.options.forEach((option) => {
      option.removeEventListener('click', this.onOptionClick);
    });

    this.closePanel();
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = null;
    }
    this._initialized = false;
  }

  onTriggerClick() {
    this.togglePanel();
  }

  onTriggerKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.togglePanel();
    }
  }

  onOptionClick(event) {
    this.onOptionSelect(event.currentTarget);
  }

  get storeCountry() {
    return this.picker.dataset.storeCountry;
  }

  get usStoreUrl() {
    return (this.picker.dataset.usStoreUrl || 'https://us.elleandriley.com').replace(/\/$/, '');
  }

  get productHandle() {
    return this.picker.dataset.productHandle;
  }

  get usRedirectPath() {
    return this.picker.dataset.usRedirectPath || '/';
  }

  get shouldVerifyProduct() {
    return this.picker.dataset.usVerifyProduct === 'true';
  }

  isOpen() {
    return this.picker.classList.contains('currency-picker--open');
  }

  openPanel() {
    document.querySelectorAll('currency-localization-form').forEach((instance) => {
      if (instance !== this) {
        instance.closePanel();
      }
    });

    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = null;
    }

    this.panel.hidden = false;
    requestAnimationFrame(() => {
      this.picker.classList.add('currency-picker--open');
    });
    this.trigger.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keyup', this.onDocumentKeyUp);
  }

  closePanel({ returnFocus = false } = {}) {
    if (!this.picker || !this.panel || !this.trigger) {
      return;
    }

    this.picker.classList.remove('currency-picker--open');
    this.trigger.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keyup', this.onDocumentKeyUp);

    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
    }

    this._closeTimeout = window.setTimeout(() => {
      if (!this.isOpen()) {
        this.panel.hidden = true;
      }

      this._closeTimeout = null;
    }, CurrencyLocalizationForm.PANEL_TRANSITION_MS);

    if (returnFocus) {
      this.trigger.focus();
    }
  }

  togglePanel() {
    if (this.isOpen()) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  onDocumentClick(event) {
    if (!this.contains(event.target)) {
      this.closePanel();
    }
  }

  onDocumentKeyUp(event) {
    if (event.key === 'Escape' && this.isOpen()) {
      event.stopPropagation();
      this.closePanel({ returnFocus: true });
    }
  }

  updateSelectedState(option) {
    this.options.forEach((item) => {
      const isSelected = item === option;
      const isLocalization = item.dataset.action === 'localization';

      item.classList.toggle('currency-picker__option--selected', isSelected);

      const indicator = item.querySelector('.currency-picker__indicator');
      if (indicator) {
        indicator.classList.toggle('currency-picker__indicator--selected', isSelected);
      }

      if (isLocalization && isSelected) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });

    if (this.triggerLabel && option.dataset.currency) {
      this.triggerLabel.textContent = option.dataset.currency;
    }
  }

  buildUsDestinationUrl(path) {
    const blockedParams = new Set([
      'preview_theme_id',
      '_ab',
      'key',
      'oseid',
      'pb',
      'country',
      'currency',
    ]);

    const usStoreBase = `${this.usStoreUrl}/`;
    const usStoreOrigin = new URL(usStoreBase).origin;
    let url;

    try {
      const isAbsoluteUrl = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(path);

      if (isAbsoluteUrl) {
        url = new URL(path);

        if (url.origin !== usStoreOrigin) {
          url = new URL('/', usStoreBase);
        }
      } else {
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        url = new URL(normalizedPath, usStoreBase);
      }
    } catch {
      url = new URL('/', usStoreBase);
    }

    blockedParams.forEach((key) => {
      url.searchParams.delete(key);
    });

    const currentParams = new URLSearchParams(window.location.search);
    currentParams.forEach((value, key) => {
      if (blockedParams.has(key) || url.searchParams.has(key)) {
        return;
      }

      url.searchParams.set(key, value);
    });

    return url.toString();
  }

  redirectToUsProduct(productHandle) {
    const productUrl = this.buildUsDestinationUrl(`/products/${productHandle}`);
    const jsonUrl = `${this.usStoreUrl}/products/${productHandle}.json`;

    fetch(jsonUrl)
      .then((res) => {
        if (res.status === 404) {
          window.location.href = `${this.usStoreUrl}/`;
          return;
        }

        window.location.href = productUrl;
      })
      .catch(() => {
        window.location.href = productUrl;
      });
  }

  redirectToUsStore() {
    const path = this.usRedirectPath;

    if (!path || path === '/') {
      window.location.href = this.buildUsDestinationUrl('/');
      return;
    }

    if (this.shouldVerifyProduct && this.productHandle) {
      this.redirectToUsProduct(this.productHandle);
      return;
    }

    window.location.href = this.buildUsDestinationUrl(path);
  }

  evaluateRouting({ countryCode, action, isUserAction }) {
    if (action === 'redirect-us') {
      this.redirectToUsStore();
      return;
    }

    if (isUserAction && countryCode) {
      this.countryInput.value = countryCode;
      this.form.submit();
    }
  }

  onOptionSelect(option) {
    const countryCode = option.dataset.countryCode;
    const action = option.dataset.action;

    if (action !== 'redirect-us') {
      this.updateSelectedState(option);
    }

    this.closePanel();

    this.evaluateRouting({
      countryCode,
      action,
      isUserAction: true,
    });
  }
}

if (!customElements.get('currency-localization-form')) {
  customElements.define('currency-localization-form', CurrencyLocalizationForm);
}
